import "./frontend.scss";
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import ReportsBarChart from "./reportsbarchart";
import loaderImage from "./includes/assets/loader.svg";
import { jsPDF } from "jspdf";
import Select from 'react-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const divsToUpdate = document.querySelectorAll(".dealer-reports-container");
divsToUpdate.forEach((div) => {
  const data = JSON.parse(div.querySelector("pre").innerText);
  const root = ReactDOM.createRoot(div);
  root.render(<Frontend {...data} />);
  div.classList.remove("dealer-reports-container");
});

function Frontend(props) {
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDealership, setSelectedDealership] = useState('');
  const [selectedCertification, setSelectedCertification] = useState('');
  const [dealershipOptions, setDealershipOptions] = useState([]);
  const [certificationOptions, setCertificationOptions] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState('reports');
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [shouldAutoDownload, setShouldAutoDownload] = useState(false);
  const downloadButtonRef = useRef(null);

  // New state for notifications tab
  const [selectedCertificationNotification, setSelectedCertificationNotification] = useState({label: 'Select Certification', value: ''});
  const [selectedDealershipNotification, setSelectedDealershipNotification] = useState({label: 'Select Dealership', value: ''});
  const [selectedFrequency, setSelectedFrequency] = useState({label: 'Daily', value: 'daily'});
  const [selectedTime, setSelectedTime] = useState({ hours: 0, minutes: 0 });
  const [selectedDate, setSelectedDate] = useState(null);

  // Check for URL parameters on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dealershipParam = urlParams.get('dealership');
    const certificationParam = urlParams.get('certification');
    const autoDownloadParam = urlParams.get('download') === 'true';
    
    if (dealershipParam) {
      setSelectedDealership(dealershipParam);
    }
    
    if (certificationParam) {
      setSelectedCertification(certificationParam);
    }
    
    if (autoDownloadParam) {
      setShouldAutoDownload(true);
    }
  }, []);

  // Auto-download PDF when data is loaded and shouldAutoDownload is true
  useEffect(() => {
    if (shouldAutoDownload && userData && !chartLoading && !pdfLoading && userData.user_display_names && userData.user_display_names.length > 0) {
      // Small delay to ensure chart is fully rendered
      const timer = setTimeout(() => {
        if (downloadButtonRef.current) {
          downloadButtonRef.current.click();
        }
        setShouldAutoDownload(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [shouldAutoDownload, userData, chartLoading, pdfLoading]);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        // Properly encode the dealership and certification parameters
        const encodedDealership = selectedDealership ? encodeURIComponent(selectedDealership) : '';
        const encodedCertification = selectedCertification ? encodeURIComponent(selectedCertification) : '';
        
        const response = await fetch(
          `/wp-json/dealer-reports/get-reports?dealership=${encodedDealership}&certification=${encodedCertification}`
        );
        if (!response.ok) {
          throw new Error("Something went wrong");
        }
        const data = await response.json();
        setUserData(data.user_data);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      }
      finally{
        setChartLoading(false);
      }
    };
    fetchReports();
  }, [selectedDealership, selectedCertification]);

  useEffect(() => {
    if (userData && dealershipOptions.length === 0) {
      const dealerships = [...new Set(userData.user_dealerships || [])];
      const certifications = [
        ...new Set(userData.user_categories.flat().map(cert => cert.term_id))
      ].map(term_id => userData.user_categories.flat().find(cert => cert.term_id === term_id));
      setDealershipOptions(dealerships);
      setCertificationOptions(certifications);
    }
  }, [userData, dealershipOptions.length]);

  // Fetch schedules when the schedules tab is selected
  useEffect(() => {
    if (selectedTab === 'schedules') {
      fetchSchedules();
    }
  }, [selectedTab]);

  const fetchSchedules = async () => {
    setSchedulesLoading(true);
    try {
      const response = await fetch('/wp-json/dealer-reports/all-schedules', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-WP-Nonce': window.dealerReportsNonce || (window.wpApiSettings && window.wpApiSettings.nonce ? window.wpApiSettings.nonce : '')
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schedules');
      }

      const data = await response.json();
      setSchedules(data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    } finally {
      setSchedulesLoading(false);
    }
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!userData) {
    return (
      <div className="reportLoaderDiv">
        <img src={loaderImage} alt="Loading" />
      </div>
    );
  }

  const dealershipData = {};
  for (let i = 0; i < userData.user_display_names.length; i++) {
    const dealership = userData.user_dealerships[i];
    const completedCourses = userData.user_completed_courses[i];

    const percentages = [];
    for (const course of completedCourses) {
      const rawPercentage = parseFloat(course.replace("%", ""));
      percentages.push(Math.round(rawPercentage * 100) / 100);
    }

    const totalSum = percentages.reduce((sum, percent) => sum + percent, 0);
    const avgPercentage =
      Math.round((totalSum / percentages.length) * 100) / 100;

    if (!dealershipData[dealership]) {
      dealershipData[dealership] = { total: 0, count: 0 };
    }
    dealershipData[dealership].total += avgPercentage;
    dealershipData[dealership].count += 1;
  }

  const chartData = [];
  for (const dealership in dealershipData) {
    const avgPercentage =
      Math.round(
        (dealershipData[dealership].total / dealershipData[dealership].count) *
          100
      ) / 100;
    chartData.push({
      name: dealership,
      completion: avgPercentage,
    });
  }

  const handleDealershipChange = (e) => {
    setChartLoading(true);
    setSelectedDealership(e.target.value);
  };
  const handleCertificationChange = (e) => {
    setChartLoading(true);
    setSelectedCertification(e.target.value);
  };

  // New handlers for notifications tab
  const handleCertificationNotificationChange = (value) => {
    setSelectedCertificationNotification(value);
  };

  const handleDealershipNotificationChange = (value) => {
    setSelectedDealershipNotification(value);
  };

  const handleFrequencyChange = (value) => {
    setSelectedFrequency(value);
  };

  const handleTimeChange = (newTime) => {
    const [hours, minutes] = newTime.split(':').map(Number);
    if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      setSelectedTime({ hours, minutes });
    }
  };

  // Format hours and minutes to always have two digits
  const formattedTime = `${String(selectedTime.hours).padStart(2, '0')}:${String(selectedTime.minutes).padStart(2, '0')}`;

  const downloadReportPdf = async () => {
    setPdfLoading(true);
    const chunkedData = [];
    for (let i = 0; i < chartData.length; i += 8) {
      chunkedData.push(chartData.slice(i, i + 8));
    }
    const doc = new jsPDF("landscape");
    const title = "Certification Completion Report";
    const pageWidth = doc.internal.pageSize.width;
    const textWidth = doc.getTextWidth(title);
    const x = (pageWidth - textWidth) / 2;
    doc.text(title, x, 10);
    const margin = 5;
    for (let index = 0; index < chunkedData.length; index++) {
      const currentChunk = chunkedData[index];
      const tempContainer = document.createElement("div");
      tempContainer.style.width = "1600px";
      tempContainer.style.height = "1600px";
      tempContainer.style.display = "none";
      document.body.appendChild(tempContainer);
      const root = ReactDOM.createRoot(tempContainer);
      root.render(<ReportsBarChart chartData={currentChunk} pdfLoading={true} />);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const chartContainer = tempContainer.querySelector(
        ".recharts-wrapper svg"
      );
      if (!chartContainer) {
        console.error("Chart rendering failed");
        document.body.removeChild(tempContainer);
        continue;
      }
      const svgData = new XMLSerializer().serializeToString(chartContainer);
      const svgBlob = new Blob([svgData], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);
          const imgData = canvas.toDataURL("image/png");
          let finalImgWidth, finalImgHeight;
          if (currentChunk.length < 8) {
            finalImgWidth = canvas.width / 5;
            finalImgHeight = canvas.height / 5;
          } else {
            const imgWidth = canvas.width * 0.264583;
            const imgHeight = canvas.height * 0.264583;
            const maxImgWidth = pageWidth - margin;
            const scaleFactor = maxImgWidth / imgWidth;
            finalImgWidth = Math.min(imgWidth, maxImgWidth);
            finalImgHeight = imgHeight * scaleFactor;
          }
          doc.addImage(
            imgData,
            "PNG",
            margin,
            20,
            finalImgWidth,
            finalImgHeight
          );
          resolve();
        };
        img.onerror = (error) => {
          console.error("Failed to load SVG as an image", error);
          reject(error);
        };
        img.src = url;
      });
      document.body.removeChild(tempContainer);
      if (index < chunkedData.length - 1) {
        doc.addPage();
      }
    }
    doc.save("Dealer_Reports.pdf");
    setPdfLoading(false);
  };

  const handleSaveSchedule = async () => {
    try {
      // Validate date is selected for non-daily frequencies
      if (selectedFrequency.value !== 'daily' && !selectedDate) {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Please select a date for weekly or monthly schedules',
          confirmButtonColor: '#007cba',
        });
        return;
      }

      // Format the date if selected
      const formattedDate = selectedDate ? 
        `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : 
        '';

      // Properly encode the parameters for the URL
      const params = new URLSearchParams();
      params.append('certification', selectedCertificationNotification.value);
      params.append('dealership', selectedDealershipNotification.value);
      params.append('frequency', selectedFrequency.value);
      params.append('time', formattedTime);
      if (formattedDate) {
        params.append('date', formattedDate);
      }

      // Add edit_request and schedule_id parameters if editing an existing schedule
      if (selectedScheduleId) {
        params.append('edit_request', 'true');
        params.append('schedule_id', selectedScheduleId);
      }

      const response = await fetch(`/wp-json/dealer-reports/create-schedule?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-WP-Nonce': window.dealerReportsNonce || (window.wpApiSettings && window.wpApiSettings.nonce ? window.wpApiSettings.nonce : '')
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create schedule');
      }

      const data = await response.json();
      
      // Show success message with SweetAlert2
      Swal.fire({
        icon: 'success',
        title: selectedScheduleId ? 'Schedule Updated!' : 'Schedule Created!',
        text: selectedScheduleId ? 'Your schedule has been updated successfully.' : 'Your schedule has been created successfully.',
        confirmButtonColor: '#007cba',
      });
      
      // Reset form
      setSelectedCertificationNotification({label: 'Select Certification', value: ''});
      setSelectedDealershipNotification({label: 'Select Dealership', value: ''});
      setSelectedFrequency({label: 'Daily', value: 'daily'});
      setSelectedTime({ hours: 0, minutes: 0 });
      setSelectedDate(null);
      setSelectedScheduleId(null);

      // If it was an update, go back to schedules tab
      if (selectedScheduleId) {
        setSelectedTab('schedules');
      }
    } catch (error) {
      console.error('Error creating schedule:', error);
      // Show error message with SweetAlert2
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to save schedule. Please try again.',
        confirmButtonColor: '#007cba',
      });
    }
  };

  return (
    <div className="dealer-report-chart-container">
      <div className="tab-buttons">
        <button 
          className={selectedTab === 'reports' ? 'active' : ''}
          onClick={() => setSelectedTab('reports')}
        >
          Reports
        </button>
        <button 
          className={selectedTab === 'notifications' ? 'active' : ''}
          onClick={() => setSelectedTab('notifications')}
        >
          Email Reports
        </button>
        <button 
          className={selectedTab === 'schedules' ? 'active' : ''}
          onClick={() => setSelectedTab('schedules')}
        >
          All Schedules
        </button>
      </div>

      {selectedTab === 'reports' && (
        <>
          <button 
            className="download-report-button" 
            onClick={downloadReportPdf} 
            disabled={chartLoading || pdfLoading || chartData.length === 0}
            ref={downloadButtonRef}
          >
            {pdfLoading ? "Generating PDF..." : "Download Report"}
          </button>
          <select name="selectedDealership" value={selectedDealership} onChange={handleDealershipChange} disabled={chartLoading || pdfLoading}>
            <option value="">All Dealership</option>
            {dealershipOptions.length > 0 ? (
              dealershipOptions.map((dealership, index) => ( 
                <option key={index} value={dealership}>
                  {dealership}
                </option>
              ))
            ) : (
              <option value="">No Dealership available</option>
            )}
          </select>
          <select name="selectedCertification" value={selectedCertification} onChange={handleCertificationChange} disabled={chartLoading || pdfLoading}>
            <option value="">All Certifications</option>
            {certificationOptions.length > 0 ? (
              certificationOptions.map((certification, index) => (
                <option key={certification.term_id} value={certification.slug}>
                  {certification.name}
                </option>
              ))
            ) : (
              <option value="">No Certifications available</option>
            )}
          </select>
          {!chartLoading ? (
            <ReportsBarChart chartData={chartData} pdfLoading={pdfLoading} />
          ) : (
            <div className="reportLoaderDiv">
              <img src={loaderImage} alt="Loading" />
            </div>
          )}
        </>
      )}

      {selectedTab === 'notifications' && (
        <div className="notifications-content">
          <h3>Create A Schedule</h3>
          
          {/* Certification Dropdown */}
          <div className="select-container">
            <span className="selector-label">All Certifications</span>
            <Select
              value={selectedCertificationNotification}
              options={[
                { label: 'All Certifications', value: '' },
                ...certificationOptions.map(cert => ({
                  label: cert.name,
                  value: cert.slug
                }))
              ]}
              onChange={handleCertificationNotificationChange}
              placeholder="Choose a certification"
            />
          </div>

          {/* Dealership Dropdown */}
          <div className="select-container">
            <span className="selector-label">All Dealerships</span>
            <Select
              value={selectedDealershipNotification}
              options={[
                { label: 'All Dealerships', value: '' },
                ...dealershipOptions.map(dealership => ({
                  label: dealership,
                  value: dealership
                }))
              ]}
              onChange={handleDealershipNotificationChange}
              placeholder="Choose a dealership"
            />
          </div>

          {/* Frequency Dropdown */}
          <div className="select-container">
            <span className="selector-label">Frequency</span>
            <Select
              value={selectedFrequency}
              options={[
                { label: 'Daily', value: 'daily' },
                { label: 'Weekly', value: 'weekly' },
                { label: 'Monthly', value: 'monthly' }
              ]}
              onChange={handleFrequencyChange}
              placeholder="Choose frequency"
            />
          </div>

          {/* Date Picker - Only shown when frequency is not daily */}
          {selectedFrequency.value !== 'daily' && (
            <div className="select-container">
              <span className="selector-label">Date</span>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                minDate={new Date()}
                dateFormat="yyyy-MM-dd"
                className="date-picker-field"
                placeholderText="Select date"
              />
            </div>
          )}

          {/* Time Picker */}
          <div className="select-container">
            <span className="selector-label">Time</span>
            <div className="time-picker-container">
              <input
                id="time-input"
                type="time"
                value={formattedTime}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="time-input-field"
              />
            </div>
          </div>

          {/* Save/Update Button Container */}
          <div className="save-button-container">
            {selectedScheduleId && (
              <button 
                className="back-button"
                onClick={() => {
                  // Reset form
                  setSelectedCertificationNotification({label: 'Select Certification', value: ''});
                  setSelectedDealershipNotification({label: 'Select Dealership', value: ''});
                  setSelectedFrequency({label: 'Daily', value: 'daily'});
                  setSelectedTime({ hours: 0, minutes: 0 });
                  setSelectedScheduleId(null);
                  setSelectedTab('schedules');
                }}
              >
                Back to Schedules
              </button>
            )}
            <button 
              className="save-schedule-button"
              onClick={handleSaveSchedule}
            >
              {selectedScheduleId ? 'Update Schedule' : 'Save Schedule'}
            </button>
          </div>
        </div>
      )}

      {selectedTab === 'schedules' && (
        <div className="schedules-content">
          <h3>All Schedules</h3>
          {schedulesLoading ? (
            <div className="reportLoaderDiv">
              <img src={loaderImage} alt="Loading" />
            </div>
          ) : (
            <div className="schedules-table-container">
              {schedules.length > 0 ? (
                <table className="schedules-table">
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>Certification</th>
                      <th>Dealership</th>
                      <th>Frequency</th>
                      <th>Time</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((schedule) => (
                      <tr key={schedule.schedule_id}>
                        <td>{schedule.sr_no}</td>
                        <td>{schedule.certification}</td>
                        <td>{schedule.dealership}</td>
                        <td>{schedule.frequency}</td>
                        <td>{schedule.time}</td>
                        <td>{schedule.frequency !== 'daily' ? 
                          new Date(schedule.date + 'T' + schedule.raw_time).toLocaleDateString('en-US', { 
                            month: '2-digit', 
                            day: '2-digit', 
                            year: 'numeric' 
                          }) : '-'}</td>
                        <td>
                          <button 
                            className="edit-schedule-btn"
                            onClick={() => {
                              // Pre-fill the form with the selected schedule data
                              setSelectedCertificationNotification({
                                label: schedule.certification,
                                value: schedule.certification_slug
                              });
                              setSelectedDealershipNotification({
                                label: schedule.dealership,
                                value: schedule.dealership
                              });
                              setSelectedFrequency({
                                label: schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1),
                                value: schedule.frequency
                              });
                              setSelectedTime({
                                hours: parseInt(schedule.raw_time.split(':')[0]),
                                minutes: parseInt(schedule.raw_time.split(':')[1])
                              });
                              // Set the date if it exists and frequency is not daily
                              if (schedule.frequency !== 'daily' && schedule.date) {
                                setSelectedDate(new Date(schedule.date));
                              } else {
                                setSelectedDate(null);
                              }
                              setSelectedScheduleId(schedule.schedule_id);
                              setSelectedTab('notifications');
                            }}
                          >
                            Edit
                          </button>
                          <button 
                            className="delete-schedule-btn"
                            onClick={async () => {
                              // Show delete confirmation with SweetAlert2
                              const result = await Swal.fire({
                                title: 'Are you sure?',
                                text: "You won't be able to revert this!",
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc3545',
                                cancelButtonColor: '#6c757d',
                                confirmButtonText: 'Yes, delete it!',
                                cancelButtonText: 'Cancel'
                              });

                              if (result.isConfirmed) {
                                try {
                                  const response = await fetch(`/wp-json/dealer-reports/delete-schedule?schedule_id=${schedule.schedule_id}`, {
                                    method: 'DELETE',
                                    credentials: 'include',
                                    headers: {
                                      'X-WP-Nonce': window.dealerReportsNonce || (window.wpApiSettings && window.wpApiSettings.nonce ? window.wpApiSettings.nonce : '')
                                    }
                                  });

                                  if (!response.ok) {
                                    throw new Error('Failed to delete schedule');
                                  }

                                  // Show success message with SweetAlert2
                                  Swal.fire({
                                    icon: 'success',
                                    title: 'Deleted!',
                                    text: 'Your schedule has been deleted successfully.',
                                    confirmButtonColor: '#007cba',
                                  });

                                  // Refresh the schedules list
                                  fetchSchedules();
                                } catch (error) {
                                  console.error('Error deleting schedule:', error);
                                  // Show error message with SweetAlert2
                                  Swal.fire({
                                    icon: 'error',
                                    title: 'Oops...',
                                    text: 'Failed to delete schedule. Please try again.',
                                    confirmButtonColor: '#007cba',
                                  });
                                }
                              }
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-schedules-message">
                  <p>No schedules found. Create a new schedule in the Notifications tab.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Frontend;